/* eslint-disable @typescript-eslint/ban-ts-comment */
import { useEffect, useState } from 'react';
import React from 'react';
import { flushSync } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from 'react-query';
import { useNavigate } from 'react-router-dom';
import { createColumnHelper } from '@tanstack/react-table';

import { useDisableTeamMember } from '@/api/workspaces/useDisableTeamMember';
import { brandColors } from '@/app/constants';
import EditPencilIcon from '@/component-library/primitives/Icons/EditPencilIcon/EditPencilIcon';
import StopIcon from '@/component-library/primitives/Icons/StopIcon/StopIcon';
import Modal from '@/component-library/primitives/Modal/Modal';
import { ProvideToasts, Toast, ToastType } from '@/component-library/primitives/Toast/Toast';
import { TeamMember } from '@/models/TeamMembers';
import { useAuth } from '@/modules/Auth/hooks/useAuth';
import queryKeys from '@/modules/shared/query/queryKeys';
import { AppRoutes } from '@/utils/routes/router';

export const useTeamMembersTable = () => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const columnHelper = createColumnHelper<TeamMember>();
  const navigate = useNavigate();
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [userEmailForDisable, setUserEmailForDisable] = useState<string | null>(null);
  const disableUserMutation = useDisableTeamMember();
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);
  const [toastInfo, setToastInfo] = useState({ message: '', toastType: '' });

  const [openDropdownId, setOpenDropdownId] = useState<string | null>(null);
  const dropdownRefs: { [rowId: string]: React.RefObject<HTMLDivElement> } = {};

  const handleDropdownClick = (event: React.MouseEvent, rowId: string) => {
    event.stopPropagation();

    if (openDropdownId === rowId) {
      setOpenDropdownId(null);
    } else {
      setOpenDropdownId(rowId);
    }
  };

  const handleDeleteModal = (state: boolean) => {
    // @ts-ignore
    if (!document.startViewTransition) {
      setShowDeleteModal(state);
    } else {
      // @ts-ignore
      document.startViewTransition(() => {
        flushSync(() => {
          setShowDeleteModal(state);
        });
      });
    }
  };

  const handleOnEdit = (email: string, givenName?: string, familyName?: string, userRole?: string) => {
    navigate(`${AppRoutes.createTeamMember}?mode=edit`, {
      state: { editEmail: email, givenName, familyName, userRole } as any,
    });
  };

  const handleOnDisable = (teamMemberEmail: string | null) => {
    setUserEmailForDisable(teamMemberEmail);
    handleDeleteModal(true);
  };

  const handleConfirmDisable = () => {
    if (userEmailForDisable) {
      disableUserMutation.mutate(userEmailForDisable, {
        onSuccess: () => {
          setTimeout(() => {
            queryClient.setQueryData<TeamMember[]>([queryKeys.teamMembers], (prevData) => {
              if (prevData) {
                return prevData.map((member) =>
                  member.email === userEmailForDisable ? { ...member, enabled: false } : member,
                );
              }
              return [];
            });
            setToastInfo({ message: t('TeamMembersPage.toastDisableSuccess'), toastType: 'success' });
            setOpen(true);
          }, 1000);
        },
        onError: () => {
          console.log('Error disabling member');
        },
        onSettled: () => {
          setUserEmailForDisable(null);
        },
      });
    }
    handleDeleteModal(false);
  };

  useEffect(() => {
    const dropdownRefKeys = Object.keys(dropdownRefs);

    const closeDropdownsExceptCurrent = (currentRowId: string | null) => {
      dropdownRefKeys.forEach((rowId) => {
        if (rowId === currentRowId) {
          setOpenDropdownId(null);
        }
      });
    };

    const handleOutsideClick = () => {
      closeDropdownsExceptCurrent(openDropdownId);
    };

    document.addEventListener('click', handleOutsideClick);

    return () => {
      document.removeEventListener('click', handleOutsideClick);
    };
  }, [openDropdownId]);

  const columns = [
    {
      id: 'fullName',
      header: () => <div>Full Name</div>,
      cell: (info: any) => {
        const givenName = info.row.original.givenName || '';
        const familyName = info.row.original.familyName || '';
        const fullName = [givenName, familyName].filter(Boolean).join(' ').trim();
        const isCurrentUser =
          info.row.original.email === user?.signInUserSession?.idToken?.payload?.email ||
          info.row.original.cognitoUserId === user?.attributes?.sub;
        return (
          <div>
            {fullName || '-'}
            {isCurrentUser ? (
              <span className="ml-1 text-xs font-medium text-custom-darkSilver">(me)</span>
            ) : null}
          </div>
        );
      },
    },
    columnHelper.accessor('email', {
      header: () => <div>{t('TeamMembersPage.emailColumn')}</div>,
      cell: (info) => <span className="block truncate max-w-[13rem]">{info.renderValue()}</span>,
    }),
    columnHelper.accessor('enabled', {
      header: () => <div>{t('TeamMembersPage.enabledColumn')}</div>,
      cell: (info) => <span className="text-custom-darkSilver">{info.row.original.enabled.toString()}</span>,
    }),
    columnHelper.accessor('created', {
      header: () => <div>{t('TeamMembersPage.createdAtColumn')}</div>,
      cell: (info) => (
        <div className="flex flex-col">
          <span>
            {new Date(info.renderValue() as string).toLocaleDateString('en-us', {
              year: 'numeric',
              month: 'short',
              day: 'numeric',
            })}
          </span>
          <span className="text-sm">
            {new Date(info.renderValue() as string).toLocaleTimeString('en-us', {
              hour: '2-digit',
              minute: '2-digit',
            })}
          </span>
        </div>
      ),
    }),
    columnHelper.accessor('status', {
      header: () => <div>{t('TeamMembersPage.statusColumn')}</div>,
      cell: (info) => {
        const status = info.renderValue() as string;
        const formatStatus = (status: string | undefined): string => {
          if (!status) return '';

          // Map status values to human-readable format
          const statusMap: Record<string, string> = {
            CONFIRMED: 'Confirmed',
            UNCONFIRMED: 'Pending confirmation',
            FORCE_CHANGE_PASSWORD: 'Force change password',
            // Handle any case variations
            confirmed: 'Confirmed',
            unconfirmed: 'Pending confirmation',
            force_change_password: 'Force change password',
            Force_Change_Password: 'Force change password',
          };

          return statusMap[status] || status;
        };

        return <div>{formatStatus(status)}</div>;
      },
    }),
    columnHelper.accessor('id', {
      id: 'actions',
      header: () => <div></div>,
      cell: (info) => {
        const rowId = info.row.original.email;
        const isCurrentUser =
          info.row.original.email === user?.signInUserSession?.idToken?.payload?.email ||
          info.row.original.cognitoUserId === user?.attributes?.sub;
        dropdownRefs[rowId] = React.createRef();

        return (
          <div className="flex gap-3" ref={dropdownRefs[rowId]}>
            {!isCurrentUser && (
              <div
                key={'0'}
                className="group/item cursor-pointer flex items-center"
                onClick={() =>
                  handleOnEdit(
                    rowId,
                    info.row.original.givenName,
                    info.row.original.familyName,
                    info.row.original.userRole,
                  )
                }
              >
                <span className="group-hover/item:[&>*_path]:stroke-custom-blue">
                  <EditPencilIcon color={brandColors.darkSilver} />
                </span>
                <span className="font-normal text-custom-darkSilver group-hover/item:text-custom-blue">
                  {t('TeamMembersPage.linkEdit')}
                </span>
              </div>
            )}
            {isCurrentUser && (
              <span className="text-xs text-custom-darkSilver self-center">
                Edit name in user menu → Manage account
              </span>
            )}

            {info.row.original.enabled === true && (
              <div
                key={'1'}
                className={`group/item flex items-center ${isCurrentUser ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
                onClick={isCurrentUser ? undefined : () => handleOnDisable(rowId)}
              >
                <span className="group-hover/item:[&>*_path]:stroke-custom-red">
                  <StopIcon color={brandColors.darkSilver} />
                </span>
                <span className="font-normal text-custom-darkSilver group-hover/item:text-custom-red">
                  {t('TeamMembersPage.linkDisable')}
                </span>
              </div>
            )}

            <ProvideToasts>
              <Toast
                isOpen={open}
                setOpen={setOpen}
                message={toastInfo.message}
                toastType={toastInfo.toastType as ToastType}
              />
            </ProvideToasts>
          </div>
        );
      },
    }),
  ];

  const deleteConfirmationModal = (
    <Modal
      isOpen={showDeleteModal}
      title={t('TeamMembersPage.modalTitle')}
      body={t('TeamMembersPage.modalBody')}
      cancelBtnTxt={t('TeamMembersPage.modalCancelBtn')}
      confirmBtnTxt={t('TeamMembersPage.modalConfirmBtn')}
      onCancel={() => handleDeleteModal(false)}
      onConfirm={handleConfirmDisable}
    />
  );

  return { columns, deleteConfirmationModal };
};
